import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Channels = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  useEffect(() => {
    // Simulate API call to fetch channels
    const fetchChannels = async () => {
      try {
        setLoading(true);
        
        // Mock data - in a real app, you would fetch from your API
        setTimeout(() => {
          // Generate mock channels
          const mockChannels = Array.from({ length: 24 }, (_, i) => ({
            id: `channel-${i}`,
            username: `streamer${i}`,
            displayName: `Streamer ${i}`,
            profileImage: `https://i.pravatar.cc/150?u=streamer${i}`,
            followers: Math.floor(Math.random() * 100000),
            isLive: i % 3 === 0, // Every third streamer is live
            category: i % 4 === 0 ? 'FPS' : i % 4 === 1 ? 'MOBA' : i % 4 === 2 ? 'RPG' : 'Strategy',
            viewerCount: i % 3 === 0 ? Math.floor(Math.random() * 10000) : 0,
            lastLive: i % 3 === 0 ? new Date().toISOString() : new Date(Date.now() - Math.floor(Math.random() * 604800000)).toISOString(), // Within last week
            bio: `Competitive ${i % 4 === 0 ? 'FPS' : i % 4 === 1 ? 'MOBA' : i % 4 === 2 ? 'RPG' : 'Strategy'} player and content creator. Join my streams for high-level gameplay and entertaining commentary!`,
          }));
          
          setChannels(mockChannels);
          setLoading(false);
        }, 1000);
      } catch (err) {
        console.error('Error fetching channels:', err);
        setError('Failed to load channels. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchChannels();
  }, []);
  
  // Filter channels based on filter and search query
  const filteredChannels = channels.filter(channel => {
    // First apply the live/offline filter
    if (filter === 'live' && !channel.isLive) return false;
    if (filter === 'offline' && channel.isLive) return false;
    
    // Then apply the search query filter (if any)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        channel.displayName.toLowerCase().includes(query) ||
        channel.username.toLowerCase().includes(query) ||
        channel.category.toLowerCase().includes(query)
      );
    }
    
    return true;
  });
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-block py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Channels</h1>
          <div className="mt-3 sm:mt-0 sm:ml-4">
            <div className="flex rounded-md shadow-sm">
              <div className="relative flex-grow focus-within:z-10">
                <input
                  type="text"
                  name="search"
                  id="search"
                  className="block w-full rounded-none rounded-l-md border-gray-300 pl-3 pr-12 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Search channels..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                onClick={() => setSearchQuery('')}
              >
                {searchQuery ? 'Clear' : 'Search'}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <p className="mt-2 text-sm text-gray-700">
              Showing {filteredChannels.length} channels
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <div className="flex items-center">
              <span className="mr-2 text-sm text-gray-700">Filter:</span>
              <select
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Channels</option>
                <option value="live">Live Now</option>
                <option value="offline">Offline</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredChannels.map((channel) => (
            <motion.div
              key={channel.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <Link to={`/user/${channel.username}`} className="block hover:opacity-90 transition-opacity">
                <div className="h-24 bg-gradient-to-r from-blue-500 to-purple-600 relative">
                  {channel.isLive && (
                    <span className="absolute top-2 left-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      LIVE NOW
                    </span>
                  )}
                  <div className="absolute -bottom-10 left-4">
                    <img
                      className="h-20 w-20 rounded-full ring-4 ring-white"
                      src={channel.profileImage}
                      alt={channel.displayName}
                    />
                  </div>
                </div>
                <div className="px-4 pt-12 pb-4">
                  <h3 className="text-lg font-medium text-gray-900">{channel.displayName}</h3>
                  <p className="text-sm text-gray-500">@{channel.username}</p>
                  
                  <div className="mt-2">
                    <span className="text-sm text-gray-600">
                      {channel.followers.toLocaleString()} followers
                    </span>
                    {channel.isLive && (
                      <span className="ml-2 text-sm text-red-600">
                        • {channel.viewerCount.toLocaleString()} watching
                      </span>
                    )}
                  </div>
                  
                  <div className="mt-2 text-sm text-gray-500 line-clamp-2" title={channel.bio}>
                    {channel.bio}
                  </div>
                  
                  <div className="mt-4 flex flex-wrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2 mb-2">
                      {channel.category}
                    </span>
                    {channel.isLive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2 mb-2">
                        Streaming Now
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2 mb-2">
                        Last live {new Date(channel.lastLive).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        
        {filteredChannels.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No channels found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter to find what you're looking for.
            </p>
            <div className="mt-6">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => {
                  setSearchQuery('');
                  setFilter('all');
                }}
              >
                Clear filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Channels; 